import type { CarouselSlide } from "@/features/opportunities/types/opportunities.types"
import { GCS_PUB_DATA_BASE } from "@/lib/creative-asset-urls"

export const GCS_BASE = GCS_PUB_DATA_BASE

/** Shared by both card mappers — the ad-library link is kept on the redesigned
 *  card (moved to the top-right of the media block) for both Competitors and
 *  My Brand, even though the Figma mock omits it. */
export const AD_LIB_BASE = "https://www.facebook.com/ads/library/?id="

export function stripAdIdPrefix(adId: string): string {
  return adId.replace(/^lib_id:/, "")
}

/** Builds the Facebook Ad Library URL for a row's ad_id, or "#" when absent. */
export function buildAdLibUrl(adId: string): string {
  return adId ? `${AD_LIB_BASE}${stripAdIdPrefix(adId)}` : "#"
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

export function extractString(val: unknown): string {
  if (val === null || val === undefined) return ""
  if (typeof val === "object" && "value" in (val as object)) {
    return String((val as { value: unknown }).value ?? "")
  }
  return String(val)
}

/** Coerce a BQ numeric cell to number|null; null on missing/NaN (e.g. a SAFE_DIVIDE zero-denominator result). */
export function extractNumber(val: unknown): number | null {
  if (val === null || val === undefined) return null
  const n = Number(val)
  return Number.isNaN(n) ? null : n
}

export function formatYmd(str: string): string {
  if (!str) return "—"
  const parts = str.split("-")
  const year = parseInt(parts[0] ?? "0", 10)
  const month = parseInt(parts[1] ?? "0", 10) - 1
  const day = parseInt(parts[2] ?? "0", 10)
  if (month < 0 || month > 11 || !day || !year) return str
  return `${MONTHS[month]} ${day}, ${year}`
}

/** Card footer range: `01.01.26`. Leaves table/chart `formatYmd` (`Jan 1, 2026`) unchanged. */
export function formatYmdDotted(str: string): string {
  if (!str) return "—"
  const parts = str.split("-")
  const year = parseInt(parts[0] ?? "0", 10)
  const month = parseInt(parts[1] ?? "0", 10)
  const day = parseInt(parts[2] ?? "0", 10)
  if (month < 1 || month > 12 || !day || !year) return str
  const dd = String(day).padStart(2, "0")
  const mm = String(month).padStart(2, "0")
  const yy = String(year).slice(-2)
  return `${dd}.${mm}.${yy}`
}

/** Parses a 'YYYY-MM-DD' string into UTC year/month/day parts, or null if malformed. */
function parseYmdUtc(str: string): { y: number; m: number; d: number } | null {
  const parts = str.split("-")
  const y = parseInt(parts[0] ?? "", 10)
  const m = parseInt(parts[1] ?? "", 10) - 1
  const d = parseInt(parts[2] ?? "", 10)
  if (!str || Number.isNaN(y) || m < 0 || m > 11 || Number.isNaN(d) || !d) return null
  return { y, m, d }
}

/**
 * Inclusive calendar-day span between two 'YYYY-MM-DD' strings, matching the "N days
 * active" semantic (a creative seen on only one day reads as "1 day active", not "0").
 * This is a CLIENT-SIDE derivation and is NOT guaranteed to match Competitors'
 * backend-computed `Days` field (different source data, different computation) — the
 * two pills can look identical but carry different meanings; that's a known, accepted
 * tradeoff (My Brand has no equivalent backend field), not a bug.
 *
 * - Same day (min === max): returns 1 (inclusive).
 * - Either string empty/unparseable: returns null — the caller MUST hide the pill
 *   entirely, never render "0 days active" (that would misrepresent missing data as
 *   a real zero).
 * - Reversed order (min date > max date): defensive-clamped via Math.abs — BigQuery's
 *   MIN()/MAX() aggregates should never produce this, but a negative day count must
 *   never render.
 */
export function daysBetweenYmd(minDate: string, maxDate: string): number | null {
  const min = parseYmdUtc(minDate)
  const max = parseYmdUtc(maxDate)
  if (!min || !max) return null
  const minMs = Date.UTC(min.y, min.m, min.d)
  const maxMs = Date.UTC(max.y, max.m, max.d)
  return Math.abs(maxMs - minMs) / 86_400_000 + 1
}

export function parseCarouselSlides(row: Record<string, unknown>, competitor: string): CarouselSlide[] | null {
  if (extractString(row.object_type).toLowerCase() !== "carousel") return null
  const mainHash = extractString(row.hash)
  if (!mainHash) return null

  // Slide 0: main hash — type is unknown, so first try .png (image); lightbox falls back to .mp4 on error
  const slides: CarouselSlide[] = [{
    hash: mainHash,
    type: "image",
    thumbnailUrl: `${GCS_BASE}/${competitor}/thumbnails/${mainHash}.png`,
    assetUrl: `${GCS_BASE}/${competitor}/assets/${mainHash}.png`,
  }]

  // Slides 1+: additional hashes with reliably known types from included_hashes_types
  const additionalHashes = extractString(row.included_hashes).split("|").filter(Boolean)
  const types = extractString(row.included_hashes_types).split("|")
  for (let i = 0; i < additionalHashes.length; i++) {
    const hash = additionalHashes[i]!
    const isVid = (types[i] ?? "image").toLowerCase() !== "image"
    slides.push({
      hash,
      type: isVid ? "video" : "image",
      thumbnailUrl: `${GCS_BASE}/${competitor}/thumbnails/${hash}.png`,
      assetUrl: `${GCS_BASE}/${competitor}/assets/${hash}.${isVid ? "mp4" : "png"}`,
    })
  }

  return slides
}
